import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  bg: "#0A0A0A", surface: "#141414", primary: "#F2ECE4", secondary: "#A6A29C",
  muted: "#737373", chili: "#E54D2E", gold: "#C1A063",
};

// Composants typographiques utilisés par ReactMarkdown
const MD_COMPONENTS = {
  h1: ({ node, ...p }) => (
    <h2 className="font-heading text-2xl md:text-3xl mt-12 mb-4 tracking-tight" style={{ color: COLORS.primary }} {...p} />
  ),
  h2: ({ node, ...p }) => (
    <h2 className="font-heading text-2xl md:text-3xl mt-12 mb-4 tracking-tight" style={{ color: COLORS.primary }} {...p} />
  ),
  h3: ({ node, ...p }) => (
    <h3 className="font-heading text-xl md:text-2xl mt-8 mb-3 tracking-tight" style={{ color: COLORS.primary }} {...p} />
  ),
  p: ({ node, ...p }) => (
    <p className="font-body text-base leading-relaxed my-4" style={{ color: COLORS.secondary }} {...p} />
  ),
  strong: ({ node, ...p }) => (
    <strong style={{ color: COLORS.primary, fontWeight: 500 }} {...p} />
  ),
  em: ({ node, ...p }) => (
    <em style={{ color: COLORS.muted, fontStyle: "italic" }} {...p} />
  ),
  ul: ({ node, ...p }) => (
    <ul className="my-4 ml-6 space-y-1.5 list-disc" style={{ color: COLORS.secondary }} {...p} />
  ),
  li: ({ node, ...p }) => (
    <li className="font-body text-base leading-relaxed marker:text-[#C1A063]" {...p} />
  ),
  a: ({ node, ...p }) => (
    <a className="hover:underline transition-colors" style={{ color: COLORS.gold }} target={p.href?.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" {...p} />
  ),
  blockquote: ({ node, ...p }) => (
    <blockquote className="my-6 pl-6 border-l-2 italic text-lg md:text-xl" style={{ borderColor: COLORS.gold, color: COLORS.primary }} {...p} />
  ),
  hr: () => <hr className="my-10 border-t" style={{ borderColor: "rgba(242,236,228,0.1)" }} />,
  code: ({ node, ...p }) => (
    <code className="px-1.5 py-0.5 rounded text-sm font-mono" style={{ background: "rgba(193,160,99,0.1)", color: COLORS.gold }} {...p} />
  ),
};

function LegalPageRenderer({ slug, fallbackTitle, fallbackEyebrow, testid }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/shop/legal-pages/${slug}`)
      .then((r) => r.json())
      .then(setPage)
      .catch(() => setPage({ slug, title: fallbackTitle, eyebrow: fallbackEyebrow, content_md: "" }))
      .finally(() => setLoading(false));
  }, [slug, fallbackTitle, fallbackEyebrow]);

  if (loading) {
    return <div className="min-h-[40vh] flex items-center justify-center text-xs uppercase tracking-widest" style={{ color: COLORS.muted }}>Chargement…</div>;
  }

  const title = page?.title || fallbackTitle;
  const eyebrow = page?.eyebrow || fallbackEyebrow;
  const md = page?.content_md || "";

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid={testid}>
      {eyebrow && (
        <p className="text-xs tracking-[0.3em] uppercase mb-6" style={{ color: COLORS.gold }}>
          {eyebrow}
        </p>
      )}
      <h1 className="font-heading text-4xl md:text-5xl tracking-tight leading-[1.1] mb-12" style={{ color: COLORS.primary }}>
        {title}
      </h1>
      {md ? (
        <div className="prose-shop">
          <ReactMarkdown components={MD_COMPONENTS}>{md}</ReactMarkdown>
        </div>
      ) : (
        <p className="italic" style={{ color: COLORS.muted }}>
          Cette page n'a pas encore été éditée. Rendez-vous dans l'admin <code>/parametres/pages-legales</code> pour la compléter.
        </p>
      )}
      <div className="mt-16 pt-8 border-t" style={{ borderColor: "rgba(242,236,228,0.08)" }}>
        <Link to="/boutique" className="text-xs uppercase tracking-[0.2em]" style={{ color: COLORS.gold }} data-testid="back-to-shop">
          ← Retour à la boutique
        </Link>
      </div>
    </div>
  );
}

export function MentionsPage() {
  return <LegalPageRenderer slug="mentions" fallbackTitle="Mentions légales" fallbackEyebrow="Impressum" testid="mentions-page" />;
}
export function CgvPage() {
  return <LegalPageRenderer slug="cgv" fallbackTitle="Conditions générales de vente" fallbackEyebrow="CGV" testid="cgv-page" />;
}
export function PrivacyPage() {
  return <LegalPageRenderer slug="confidentialite" fallbackTitle="Politique de confidentialité" fallbackEyebrow="Données personnelles" testid="privacy-page" />;
}
export function StoryPage() {
  return <LegalPageRenderer slug="histoire" fallbackTitle="Notre histoire" fallbackEyebrow="L'âme de Maison Thaï Conthey" testid="story-page" />;
}
